import type { TJSONSchema } from "@artifact-management/json-dynamic-form";
import type { ISettingValueMaster } from "@bbj-types/setting";
import { defaultRenderValue } from "@inputs/autocomplete/Autocomplete/tags";
import { AutocompleteCheckboxes } from "@inputs/autocomplete/AutocompleteCheckboxes";
import { Autocomplete, TextField } from "@mui/material";
import { Controller, type FieldValues, type Path, type PathValue } from "react-hook-form";
import type { IFormGenInputProps } from "./index";
import { getFormGenEnumLabels, getFormGenEnumOptionKey, getFormGenEnumOptions } from "./logics";

export const FormGenInputAutocomplete = <Schema extends TJSONSchema, T extends FieldValues>({
  control,
  uiSchemaElement,
  jsonSchemaElement,
  multiple,
  enumOptions,
}: IFormGenInputProps<Schema, T>) => {
  const options = enumOptions;
  const labels = getFormGenEnumLabels(jsonSchemaElement, multiple);
  const isGroupedMultiple = multiple && options.some((option) => (option as Partial<ISettingValueMaster>)?.group);

  if (isGroupedMultiple) {
    return (
      <Controller
        control={control}
        name={uiSchemaElement.scope as unknown as Path<T>}
        defaultValue={[] as unknown as PathValue<T, Path<T>>}
        render={({ field, fieldState }) => (
          <AutocompleteCheckboxes
            value={field.value ?? []}
            onChange={(_, value) => field.onChange(value)}
            onBlur={field.onBlur}
            disabled={field.disabled}
            options={options as Pick<ISettingValueMaster, "label" | "value" | "group">[]}
            size="small"
            fullWidth
            TextFieldProps={{
              label: uiSchemaElement.label,
              name: field.name,
              error: Boolean(fieldState.error),
              helperText: fieldState.error?.message,
              inputRef: field.ref,
            }}
          />
        )}
      />
    );
  }

  return (
    <Controller
      control={control}
      name={uiSchemaElement.scope as unknown as Path<T>}
      defaultValue={(multiple ? [] : undefined) as unknown as PathValue<T, Path<T>>}
      render={({ field, fieldState }) => (
        <Autocomplete
          value={field.value ?? (multiple ? [] : null)}
          onChange={(_, value) => {
            field.onChange(value ?? undefined);
          }}
          options={options}
          isOptionEqualToValue={(option, value) => getFormGenEnumOptionKey(option) === getFormGenEnumOptionKey(value)}
          onBlur={field.onBlur}
          disabled={uiSchemaElement.disabled ?? field.disabled}
          renderInput={(params) => (
            <TextField
              {...params}
              label={uiSchemaElement.label}
              name={field.name}
              error={Boolean(fieldState.error)}
              helperText={fieldState.error?.message}
              inputRef={field.ref}
            />
          )}
          renderValue={
            multiple
              ? (values, getItemProps) =>
                  defaultRenderValue(
                    getFormGenEnumOptions(values as unknown[], labels).map(({ key, label }) => ({ label, value: key })),
                    getItemProps
                  )
              : undefined
          }
          limitTags={1}
          multiple={multiple}
          disableCloseOnSelect={multiple}
          size="small"
          fullWidth
        />
      )}
    />
  );
};
